import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    const handleClick = (event) => {
        if (!(event.target instanceof Element)) return;

        const closeButton = event.target.closest('.mega-menu__close');
        if (closeButton) {
          event.preventDefault();
          this.closeMenu(event);
          return;
        }

        const tab = event.target.closest('.mega-menu__tab, .mega-menu__top-link');
        if (tab) {
          const title = tab.getAttribute('data-menu-title');
          if (title) {
            event.preventDefault();
            event.stopPropagation();
            this.#switchToTitle(title);
          }
          return;
        }

        const menuLink = event.target.closest('a.menu-list__link[data-has-submenu="true"]');
        if (menuLink instanceof HTMLElement) {
          event.preventDefault();
          event.stopPropagation();
          this.#setActiveItem(menuLink, true);
        }
      };

    this.addEventListener('click', handleClick, {
      signal: this.#abortController.signal,
      capture: true,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate by title from tab buttons.
   * @param {Event} event
   */
  activateByTitle = (event) => {
    this.#debouncedDeactivate.cancel();
    if (!(event.target instanceof Element)) return;
    const title = event.target.getAttribute('data-menu-title');
    if (!title) return;
    event.preventDefault();
    event.stopPropagation();
    this.#switchToTitle(title);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    const isDefaultSlot = event.target.slot === '';
    this.#setActiveItem(item, isDefaultSlot);
  };

  /**
   * Activate a menu item programmatically.
   * @param {HTMLElement | null} item
   * @param {boolean} isDefaultSlot
   */
  #setActiveItem = (item, isDefaultSlot) => {
    if (!item || item === this.#state.activeItem) return;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const container = item.closest('.menu-list__list-item');
    const activeTitle = container?.getAttribute('data-menu-title');

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    if (submenu && activeTitle) {
      const tabs = submenu.querySelectorAll('.mega-menu__tab');
      tabs?.forEach((tab) => {
        const tabTitle = tab.getAttribute('data-menu-title');
        tab.classList.toggle('mega-menu__tab--active', tabTitle === activeTitle);
      });
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Activate a menu item by title handle.
   * @param {string} title
   */
  #activateByTitle = (title) => {
    this.#debouncedDeactivate.cancel();
    this.#switchToTitle(title);
  };

  /**
   * Force activate by title, bypassing ref lookup.
   * @param {string} title
   */
  #switchToTitle = (title) => {
    const safeTitle = escapeSelector(title);
    const items = this.querySelectorAll('.menu-list__list-item');
    let matchedLink = null;
    let matchedSubmenu = null;
    items.forEach((listItem) => {
      const link = listItem.querySelector('[ref="menuitem"]');
      const submenu = listItem.querySelector('[ref="submenu[]"]');
      const isMatch = listItem.getAttribute('data-menu-title') === safeTitle;
      if (link instanceof HTMLElement) {
        link.setAttribute('aria-expanded', isMatch ? 'true' : 'false');
        if (isMatch) {
          matchedLink = link;
        }
      }
      if (submenu instanceof HTMLElement) {
        if (isMatch) {
          submenu.dataset.active = '';
          submenu.style.visibility = 'visible';
          submenu.style.transform = 'translateX(0)';
          matchedSubmenu = submenu;
        } else {
          delete submenu.dataset.active;
          submenu.style.visibility = 'hidden';
          submenu.style.transform = 'translateX(-100%)';
        }
      }
    });
    if (matchedLink) {
      this.#state.activeItem = matchedLink;
      this.ariaExpanded = 'true';
      matchedLink.focus({ preventScroll: true });
    }
    if (matchedSubmenu) {
      const submenuHeight = matchedSubmenu.offsetHeight;
      this.style.setProperty('--submenu-height', `${submenuHeight}px`);
      this.style.setProperty('--submenu-opacity', '1');
    }
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;
    if (event instanceof FocusEvent) {
      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Element && this.contains(nextTarget)) {
        return;
      }
    }

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    this.#clearForcedSubmenus();

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Clear any forced submenu inline styles.
   */
  #clearForcedSubmenus = () => {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    submenus.forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;
      submenu.style.visibility = '';
      submenu.style.transform = '';
    });
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Close the active menu immediately
   * @param {Event} [event]
   */
  closeMenu = (event) => {
    event?.preventDefault?.();
    this.#debouncedDeactivate.cancel();
    this.#deactivate();
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Escape a string for use in a CSS selector.
 * @param {string} value
 * @returns {string}
 */
function escapeSelector(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/"/g, '\\"');
}
